import { collection, doc, getDocs, query, updateDoc, where, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { FirebasePatientService } from './firebase/FirebasePatientService';
import {
  ComplianceStatus,
  evaluateForm,
  evaluateHrDocument,
} from '../utils/compliance';

export interface ComplianceItem {
  /** form_assignments or hr_documents doc id */
  id: string;
  kind: 'patient_form' | 'staff_document';
  subjectId: string;
  subjectName: string;
  label: string;
  status: ComplianceStatus;
  /** ISO date the item is, or was, due. */
  dueDate?: string;
}

const IN_QUERY_LIMIT = 30; // Firestore caps `in` at 30 values

const toIso = (value: any): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  return value.toDate?.()?.toISOString();
};

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/**
 * Builds the Legals list: patient forms that are missing, overdue or due for
 * their yearly renewal, and staff documents that are missing or expiring.
 * Computed live from Firestore each time, so it never goes stale.
 */
export async function loadComplianceItems(practiceOwnerId: string, now: Date = new Date()): Promise<ComplianceItem[]> {
  const items: ComplianceItem[] = [];

  // Patients (names + the set of clients this practice owns)
  const patients = await FirebasePatientService.getAllPracticePatients();
  const nameById = new Map<string, string>();
  patients.forEach((p) => nameById.set(p.id, `${p.firstName} ${p.lastName}`.trim() || 'Unnamed patient'));

  for (const ids of chunk(Array.from(nameById.keys()), IN_QUERY_LIMIT)) {
    const snap = await getDocs(query(collection(db, 'form_assignments'), where('clientId', 'in', ids)));
    snap.docs.forEach((d) => {
      const a = d.data();
      const result = evaluateForm(
        {
          formTemplateId: a.formTemplateId,
          category: a.category,
          required: a.required,
          completed: !!a.completed,
          completedDate: toIso(a.completedDate),
          dueDate: toIso(a.dueDate),
        },
        now
      );
      if (!result) return;
      items.push({
        id: d.id,
        kind: 'patient_form',
        subjectId: a.clientId,
        subjectName: nameById.get(a.clientId) || 'Unknown patient',
        label: a.formTemplateName || a.formTemplateId,
        status: result.status,
        dueDate: result.dueDate,
      });
    });
  }

  // Staff documents (training certificates, licenses, ...)
  if (practiceOwnerId) {
    const snap = await getDocs(query(collection(db, 'hr_documents'), where('practiceOwnerId', '==', practiceOwnerId)));
    snap.docs.forEach((d) => {
      const h = d.data();
      const result = evaluateHrDocument(
        { status: h.status, dueDate: toIso(h.dueDate), expirationDate: toIso(h.expirationDate) },
        now
      );
      if (!result) return;
      items.push({
        id: d.id,
        kind: 'staff_document',
        subjectId: h.employeeId,
        subjectName: h.employeeName || 'Staff member',
        label: h.formName || h.documentType,
        status: result.status,
        dueDate: result.dueDate,
      });
    });
  }

  const order: Record<ComplianceStatus, number> = { overdue: 0, due_soon: 1, incomplete: 2 };
  items.sort((a, b) => order[a.status] - order[b.status] || (a.dueDate || '').localeCompare(b.dueDate || ''));
  return items;
}

/**
 * Reopen a signed patient form for its yearly renewal. The patient sees it
 * again in their portal exactly like a newly assigned form.
 */
export async function requestFormRenewal(assignmentId: string, daysToComplete = 14): Promise<void> {
  const due = new Date();
  due.setDate(due.getDate() + daysToComplete);
  await updateDoc(doc(db, 'form_assignments', assignmentId), {
    completed: false,
    completedDate: null,
    dueDate: Timestamp.fromDate(due),
    updatedAt: Timestamp.now(),
  });
}
